import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Check, Copy, Crown, Gift, Info, Share2, Users } from "lucide-react";
import WebHeader from "./WebHeader"; // adjust path
import useGoerOneAuth from "../../hooks/useGoerOneAuth";
import { fetchLoyaltyPrograms } from "../../store/loyaltySlices/loyaltyCardsSlice";

// TODO: replace with the real referral/registration link later
const getReferLink = () => (typeof window !== "undefined" ? window.location.href : "");

const formatINR = (v) => `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// Alternating card colours (blue, purple, ...)
const CARD_THEMES = [
    { card: "bg-blue-50 border-blue-100", icon: "bg-blue-100 text-blue-600", coin: "text-[#0B67FC]" },
    { card: "bg-purple-50 border-purple-100", icon: "bg-purple-100 text-purple-600", coin: "text-purple-600" },
    { card: "bg-amber-50 border-amber-100", icon: "bg-amber-100 text-amber-600", coin: "text-amber-600" },
];

export default function ReferAndEarn({ customer }) {
    const dispatch = useDispatch();
    const { user: testUser, loginError, refreshUser } = useGoerOneAuth(
        customer?.id && customer?.mobile ? { id: customer.id, mobile: customer.mobile } : null
    );

    const [copied, setCopied] = useState("");
    const [programs, setPrograms] = useState([]);
    const [programsLoading, setProgramsLoading] = useState(true);

    const referCode = testUser?.details?.referral_code || "";
    const referLink = getReferLink();

    // Fetch loyalty programs
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await dispatch(fetchLoyaltyPrograms()).unwrap();
                // handle both shapes: full API response or already-unwrapped array
                const list = Array.isArray(res) ? res : res?.data ?? [];
                if (active) setPrograms(list);
            } catch (e) {
                console.error("Failed to load loyalty programs", e);
            } finally {
                if (active) setProgramsLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [dispatch]);

    // Only active programs that actually give a referral reward
    const rewardPrograms = useMemo(
        () => programs.filter((p) => p?.is_active && Number(p?.referral_coin) > 0),
        [programs]
    );

    const shareMessage =
        `*Join me on GoerOne!*\n\n` +
        `I'm inviting you to GoerOne, where every purchase brings you rewards \n\n` +
        `Use my referral code *${referCode}* while registering\n` +
        `or simply open this link:\n${referLink}\n\n` +
        `Sign up today and let's both earn rewards!`;

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;

    const copy = async (text, key) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(key);
            setTimeout(() => setCopied(""), 2000);
        } catch (e) {
            console.error("Copy failed", e);
        }
    };

    const priceLabel = (p) =>
        p.membership_type === "paid" && Number(p.membership_amount) > 0
            ? formatINR(p.membership_amount)
            : "Free";

    return (
        <div className="min-h-screen bg-gray-50">
            <WebHeader name={customer?.name} />

            <main className="mx-auto px-4 py-4 sm:px-4">
                {/* Hero */}
                <section className="overflow-hidden rounded-xl bg-gradient-to-br from-[#0B67FC] to-[#5B9DFF] p-6 text-white shadow-lg sm:p-10">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20">
                        <Gift size={28} />
                    </div>
                    <h1 className="mt-4 text-2xl font-bold sm:text-3xl">Refer & Earn</h1>
                    <p className="mt-2 max-w-md text-sm text-white/90 sm:text-base">
                        Invite your friends and family to GoerOne. When they join using your code, you both earn rewards.
                    </p>
                </section>

                {/* What you'll earn (above referral code) */}
                {programsLoading ? (
                    <section className="mt-6 space-y-3">
                        <div className="h-20 animate-pulse rounded-2xl bg-gray-100" />
                        <div className="h-20 animate-pulse rounded-2xl bg-gray-100" />
                    </section>
                ) : (
                    rewardPrograms.length > 0 && (
                        <section className="mt-3">
                            <h2 className="text-lg font-bold text-gray-800">What you'll earn</h2>
                            <div className="mt-2 space-y-2">
                                {rewardPrograms.map((p, i) => {
                                    const theme = CARD_THEMES[i % CARD_THEMES.length];
                                    return (
                                        <div
                                            key={p.program_id}
                                            className={`flex items-center gap-3 rounded-xl border p-4 ${theme.card}`}
                                        >
                                            <div
                                                className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl ${theme.icon}`}
                                            >
                                                <Crown size={28} />
                                            </div>
                                            <div>
                                                <p className="font-semibold text-gray-900">
                                                    {p.program_name} • {priceLabel(p)}
                                                </p>
                                                <p className="text-sm text-gray-700">
                                                    You will earn{" "}
                                                    <span className={`font-bold ${theme.coin}`}>
                                                        {Number(p.referral_coin).toLocaleString("en-IN")} GoerCoin
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="mt-4 flex items-start gap-2 text-sm text-gray-600">
                                <Info size={18} className="mt-0.5 shrink-0 text-[#0B67FC]" />
                                <p>Pending when your friend joins. Released after their first successful transaction.</p>
                            </div>
                        </section>
                    )
                )}

                {/* Code card */}
                <section className="mt-3 rounded-xl border border-gray-100 bg-white p-4 shadow-md sm:p-6">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Your referral code</p>

                    {loginError ? (
                        <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">
                            <span>Couldn't load your referral code.</span>
                            <button onClick={() => refreshUser?.()} className="font-semibold underline">
                                Retry
                            </button>
                        </div>
                    ) : !testUser ? (
                        <div className="mt-3 h-14 animate-pulse rounded-xl bg-gray-100" />
                    ) : (
                        <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border-2 border-dashed border-[#0B67FC]/40 bg-[#0B67FC]/5 px-4 py-3">
                            <span className="text-xl font-extrabold tracking-widest text-[#0B67FC]">
                                {referCode || "—"}
                            </span>
                            <button
                                onClick={() => copy(referCode, "code")}
                                disabled={!referCode}
                                className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-[#0B67FC] shadow-sm transition hover:bg-[#0B67FC] hover:text-white disabled:opacity-50"
                            >
                                {copied === "code" ? <Check size={16} /> : <Copy size={16} />}
                                {copied === "code" ? "Copied" : "Copy"}
                            </button>
                        </div>
                    )}

                    <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                        <a
                            href={whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-disabled={!referCode}
                            onClick={(e) => !referCode && e.preventDefault()}
                            className={`flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:brightness-95 ${
                                !referCode ? "pointer-events-none opacity-50" : ""
                            }`}
                        >
                            <Share2 size={18} />
                            Share on WhatsApp
                        </a>
                        <button
                            onClick={() => copy(referLink, "link")}
                            className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition hover:border-[#0B67FC] hover:text-[#0B67FC]"
                        >
                            {copied === "link" ? <Check size={18} /> : <Copy size={18} />}
                            {copied === "link" ? "Link copied" : "Copy link"}
                        </button>
                    </div>
                </section>

                {/* How it works */}
                <section className="mt-5 rounded-xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
                    <h2 className="flex items-center gap-2 text-lg font-bold text-gray-800">
                        <Users size={20} className="text-[#0B67FC]" /> How it works
                    </h2>
                    <ol className="mt-3 space-y-3">
                        {[
                            ["Share your code", "Invite friends to GoerOne by sharing your referral code or link on WhatsApp."],
                            ["Friend joins", "Your friend registers using your referral code or referral link and completes their first transaction."],
                            ["Earn rewards", "You and your friend both earn rewards when they complete their first transaction."]
                        ].map(([title, desc], i) => (
                            <li key={title} className="flex gap-3">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0B67FC] text-sm font-bold text-white">
                                    {i + 1}
                                </span>
                                <div>
                                    <p className="font-semibold text-gray-800">{title}</p>
                                    <p className="text-sm text-gray-500">{desc}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </section>

                {/* Terms & Conditions */}
                <section className="mt-5 rounded-xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
                    <h2 className="text-lg font-bold text-gray-800">Terms & Conditions</h2>
                    <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-gray-600">
                        <li>1 GoerCoin = ₹1.</li>
                        <li>Referral rewards are released only after your friend's first successful transaction.</li>
                        {rewardPrograms.map((p) => (
                            <li key={p.program_id}>
                                {p.max_referral_account ? (
                                    <>
                                        <span className="font-semibold">{p.program_name}:</span> you can earn the referral
                                        reward up to {p.max_referral_account}{" "}
                                        {Number(p.max_referral_account) === 1 ? "time" : "times"}. Referrals beyond that
                                        limit will not earn a reward.
                                    </>
                                ) : (
                                    <>
                                        <span className="font-semibold">{p.program_name}:</span> no limit on the number
                                        of rewarded referrals.
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            </main>
        </div>
    );
}